function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useEffect, useMemo, useState } from 'react';
import { keyBy } from 'lodash-es';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';
import { Button, CustomModal, FilterPickerGroup, MultiSelectFilterPicker, PageEmptyState, PageErrorState, PageHeader, Searchbox, Spinner, TableList, Typography } from 'seagull';
import { CreateProposalButton } from "../../../components/CreateProposalButton/CreateProposalButton";
import { modalSize } from "../../../infra/modals";
import { handleRequest } from "../../../infra/utils";
import { useAppConfig, useStore, useStoreActions } from "../../../store/store-context";
import NoDataIcon from "../../assets/NoDataIcon";
import { getAccountingDetails } from "../accounting.utils";
import styles from "./MapContactsModal.module.scss";
import { ContentState, FooterState, applyFilters, columnDefs, getModalState, getNumMappedContacts, getRelevantSortedContacts, getHeaderItems } from "./MapContactsModal.utils";
import MapContactsRow from "./MapContactsRow";
import { selectors } from "../../../store/state-selectors";
import { useContacts } from "../../../hooks/reactQuery/queries/useContacts";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// Adapter for react-window
function ContactsRowComponent(props) {
  const {
    data,
    index,
    style
  } = props;
  const integratedId = data.anchorContacts[index].integratedId({
    accounting: data.accounting
  });
  const integratedAccountingCustomer = integratedId ? data.accountingCustomersDictionary[integratedId] : undefined;
  return /*#__PURE__*/_jsx("div", {
    style: style,
    children: /*#__PURE__*/_jsx(MapContactsRow, {
      anchorContact: data.anchorContacts[index],
      accountingCustomers: data.accountingCustomers,
      integratedAccountingCustomer: integratedAccountingCustomer,
      changeContactLink: data.changeContactLink,
      createNewCustomer: data.createNewCustomer,
      testid: 'map-contacts-row',
      onInputFocus: data.onInputFocus,
      onInputBlur: data.onInputBlur,
      accounting: data.accounting
    }, data.anchorContacts[index].id())
  });
}
export function MapContactsModal(props) {
  const {
    dismissModal,
    onChange,
    contactIds,
    accounting
  } = props;
  const {
    contacts,
    isFetching,
    error: contactsError,
    refresh: fetchContacts,
    dataUpdatedAt: contactsUpdatedAt
  } = useContacts({
    refreshOnMount: true
  });
  const contactsLoadableState = useMemo(() => ({
    data: contacts,
    error: contactsError,
    loading: isFetching,
    lastSuccessfulFetch: contactsUpdatedAt
  }), [contacts, contactsError, isFetching, contactsUpdatedAt]);
  const unsortedAnchorContacts = contacts;
  const accountingCustomersLoadableState = useStore(selectors.integration.accounting(accounting).customers);
  const accountingCustomers = accountingCustomersLoadableState.data;
  const {
    api,
    fetchAccountingCustomers
  } = useStoreActions();
  const [anchorContacts, setAnchorContacts] = useState([]);
  const {
    inputDebounceTime
  } = useAppConfig();
  const [registerSearchBoxHotkey, setRegisterSearchBoxHotkey] = useState(true);
  const [search, setSearch] = useState('');
  const [contactMappingStatusFilter, setContactMappingStatusFilter] = useState([]);
  const numOfMappedContacts = useMemo(() => getNumMappedContacts(anchorContacts, accounting), [anchorContacts, accounting]);
  const {
    testIdPrefix
  } = getAccountingDetails(accounting);
  const modalState = useMemo(() => {
    return getModalState(contactsLoadableState, accountingCustomersLoadableState);
  }, [contactsLoadableState, accountingCustomersLoadableState]);
  const accountingCustomersDictionary = useMemo(() => keyBy(accountingCustomers, c => c.id()), [accountingCustomers]);
  const {
    filteredAnchorContacts,
    contactMappingStatusFilterOptions
  } = useMemo(() => applyFilters(anchorContacts, search, contactMappingStatusFilter, accounting), [anchorContacts, search, contactMappingStatusFilter, accounting]);
  useEffect(() => {
    const filteredAndSorted = getRelevantSortedContacts(unsortedAnchorContacts, contactIds || [], accounting);
    setAnchorContacts(filteredAndSorted);
  }, [contactIds, unsortedAnchorContacts, accounting]);
  useEffect(() => {
    fetchAccountingCustomers(accounting);
  }, []);
  async function refreshData() {
    fetchContacts({
      throwOnError: false
    });
    fetchAccountingCustomers(accounting);
  }
  async function changeContactLink(anchorContact, accountingCustomerId) {
    const link = await handleRequest(api.accounting.linkContact({
      contactId: anchorContact.id(),
      data: {
        integratedId: accountingCustomerId,
        version: anchorContact.version()
      },
      accounting
    }));
    if (!link.success) {
      throw link.error;
    }
    onChange && onChange();
    const updatedContact = await handleRequest(api.contact.getById(anchorContact.id()));
    if (!updatedContact.success) {
      throw updatedContact.error;
    }
    updateContact(updatedContact.data);
  }
  async function createNewCustomer(anchorContact) {
    await api.accounting.createContact({
      contactId: anchorContact.id(),
      accounting
    });
    onChange && onChange();
    const updatedContact = await api.contact.getById(anchorContact.id());
    updateContact(updatedContact);
    await fetchAccountingCustomers(accounting);
  }
  function updateContact(contact) {
    const newAnchorContacts = anchorContacts.map(c => c.id() == contact.id() ? contact : c);
    setAnchorContacts(newAnchorContacts);
  }
  function getFooter() {
    switch (modalState.footer) {
      case FooterState.Valid:
        return /*#__PURE__*/_jsxs("div", {
          className: styles.footer,
          children: [anchorContacts && /*#__PURE__*/_jsx("div", {
            "data-testid": "total-map-status",
            children: /*#__PURE__*/_jsx(Typography, {
              children: "".concat(numOfMappedContacts, " of ").concat(anchorContacts.length, " contacts mapped")
            })
          }), /*#__PURE__*/_jsx(Button, {
            onClick: dismissModal,
            "data-testid": "".concat(testIdPrefix, "-map-contacts-close"),
            children: "Close"
          })]
        });
      case FooterState.Invalid:
        return null;
    }
  }
  function getContent() {
    switch (modalState.content) {
      case ContentState.Valid:
        return /*#__PURE__*/_jsxs("div", {
          className: styles.content,
          children: [/*#__PURE__*/_jsx("div", {
            style: {
              marginBlockEnd: '24px'
            },
            children: /*#__PURE__*/_jsxs(FilterPickerGroup, {
              flexWrap: true,
              "data-testid": "map-contacts-modal-filters",
              children: [/*#__PURE__*/_jsx(Searchbox, {
                registerHotkey: registerSearchBoxHotkey,
                value: search,
                "data-testid": "map-contacts-modal-search",
                debounceTime: inputDebounceTime !== null && inputDebounceTime !== void 0 ? inputDebounceTime : 500,
                onChange: event => setSearch(event.target.value),
                onClear: () => setSearch('')
              }), /*#__PURE__*/_jsx(MultiSelectFilterPicker, {
                "data-testid": "".concat(testIdPrefix, "-mapping-status-filter-picker"),
                value: contactMappingStatusFilter,
                options: contactMappingStatusFilterOptions,
                label: "Is mapped",
                onChange: setContactMappingStatusFilter
              })]
            })
          }), /*#__PURE__*/_jsx(TableList.Header, {
            items: getHeaderItems(accounting)
          }), filteredAnchorContacts && /*#__PURE__*/_jsx("div", {
            className: styles.tableContainer,
            children: /*#__PURE__*/_jsx(AutoSizer, {
              children: _ref => {
                let {
                  height,
                  width
                } = _ref;
                return /*#__PURE__*/_jsx(FixedSizeList, {
                  itemData: {
                    accounting,
                    anchorContacts: filteredAnchorContacts,
                    accountingCustomers,
                    accountingCustomersDictionary,
                    createNewCustomer,
                    changeContactLink,
                    onInputFocus: () => setRegisterSearchBoxHotkey(false),
                    onInputBlur: () => setRegisterSearchBoxHotkey(true)
                  },
                  itemCount: filteredAnchorContacts.length,
                  itemSize: 64,
                  height: height,
                  width: width,
                  overscanCount: 50,
                  children: ContactsRowComponent
                });
              }
            })
          })]
        });
      case ContentState.Loading:
        return /*#__PURE__*/_jsx("div", {
          className: styles.loading,
          children: /*#__PURE__*/_jsx(Spinner, {
            size: 40,
            "data-testid": "map-contacts-loading"
          })
        });
      case ContentState.Empty:
        return /*#__PURE__*/_jsx(PageEmptyState, {
          "data-testid": "map-contacts-empty-state",
          illustration: /*#__PURE__*/_jsx(NoDataIcon, {}),
          message: "In order to map with your ".concat(accountingName, " account, start by creating an agreement"),
          actions: /*#__PURE__*/_jsx(CreateProposalButton, {
            text: "Create a new agreement",
            hidePrefix: true,
            "data-testid": "map-contacts-new-btn"
          })
        });
      case ContentState.Error:
        return /*#__PURE__*/_jsx(PageErrorState, {
          "data-testid": "map-contacts-error-state",
          message: "Failed to retrieve the relevant data please try again later",
          onRefresh: refreshData,
          skin: "secondary"
        });
    }
  }
  const accountingName = getAccountingDetails(accounting).fullName;
  return /*#__PURE__*/_jsx(CustomModal, _objectSpread(_objectSpread({
    "data-testid": "".concat(testIdPrefix, "-map-contacts-modal"),
    footer: getFooter()
  }, modalSize.large), {}, {
    onClose: dismissModal,
    children: /*#__PURE__*/_jsxs(Typography, {
      className: styles.modalContainer,
      children: [/*#__PURE__*/_jsx(PageHeader, {
        title: "Map contacts to ".concat(accountingName),
        subheader: "Map Anchor contacts to ".concat(accountingName, " customers.")
      }), /*#__PURE__*/_jsx(TableList.Provider, {
        columnDefs: columnDefs,
        children: getContent()
      })]
    })
  }));
}